<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import tiktokClient from 'dashboard/api/channel/tiktokClient';
import Button from 'dashboard/components-next/button/Button.vue';
import { buildSocialChannelCallbackError } from './socialChannelCallbackErrors';

const { t } = useI18n();

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const errorStateAction = ref('');
const errorStateActionLabel = ref('');
const isRequestingAuthorization = ref(false);

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const errorType = urlParams.get('error_type');
  const errorMessage = urlParams.get('error_message');
  const callbackError = buildSocialChannelCallbackError({
    channelKey: 'TIKTOK',
    errorType,
    errorMessage,
    t,
  });

  if (callbackError) {
    hasError.value = true;
    errorStateMessage.value = callbackError.message;
    errorStateDescription.value = callbackError.description;
    errorStateAction.value = callbackError.action;
    errorStateActionLabel.value = callbackError.actionLabel;
  }
  // User need to remove the error params from the url to avoid the error to be shown again after page reload, so that user can try again
  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
});

const requestAuthorization = async () => {
  isRequestingAuthorization.value = true;
  const response = await tiktokClient.generateAuthorization();
  const {
    data: { url },
  } = response;

  window.location.href = url;
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div
        v-if="hasError"
        class="max-w-lg mx-auto text-center rounded-2xl outline outline-1 outline-n-weak px-8 py-10"
        role="alert"
      >
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
          class="mt-4 text-sm text-n-slate-11"
        />
        <p v-if="errorStateAction" class="mt-4 text-sm text-n-slate-12">
          {{ errorStateAction }}
        </p>
        <Button
          class="mt-6"
          outline
          slate
          icon="i-ri-refresh-line"
          :disabled="isRequestingAuthorization"
          :is-loading="isRequestingAuthorization"
          :label="errorStateActionLabel"
          @click="requestAuthorization()"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-2xl font-medium">
          {{ $t('INBOX_MGMT.ADD.TIKTOK.CONNECT_YOUR_TIKTOK_PROFILE') }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.TIKTOK.HELP') }}
        </p>
        <Button
          class="text-white !rounded-full !px-6 bg-gradient-to-r from-[#00f2ea] via-[#ff0050] to-[#000000]"
          lg
          icon="i-ri-tiktok-line"
          :disabled="isRequestingAuthorization"
          :is-loading="isRequestingAuthorization"
          :label="$t('INBOX_MGMT.ADD.TIKTOK.CONTINUE_WITH_TIKTOK')"
          @click="requestAuthorization()"
        />
      </div>
    </div>
  </div>
</template>
